import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ChevronLeft, ChevronRight, MapPin, Crown } from "lucide-react";
import { API, imgUrl } from "../lib/api";
import { useUserLocation } from "../lib/location";

function DefaultBanner({ city, onPick }) {
  return (
    <div className="relative h-full w-full flex items-center px-6 sm:px-12 overflow-hidden text-black" style={{ background: "var(--gold-grad)" }} data-testid="banner-default">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,255,255,0.35),transparent_55%),radial-gradient(ellipse_at_bottom_left,rgba(0,0,0,0.25),transparent_55%)]" />
      <Crown className="absolute -right-6 -bottom-8 w-48 h-48 sm:w-64 sm:h-64 text-black/10" />
      <div className="relative">
        <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-black/70 mb-1">CarSaleMarket.com {city ? `· ${city}` : ""}</p>
        <h2 className="font-display text-2xl sm:text-4xl leading-tight font-semibold">{city ? <>Premium pre-owned cars in <span className="italic">{city}</span></> : <>Luxury drives, <span className="italic">honest prices.</span></>}</h2>
        {!city && onPick && <button onClick={onPick} className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-black text-[#E5C158] px-4 py-1.5 text-xs font-semibold hover:bg-black/80 transition-colors" data-testid="banner-pick-city"><MapPin className="w-3.5 h-3.5" />Choose your city</button>}
      </div>
    </div>
  );
}

function Slide({ b }) {
  const inner = (
    <>
      <img src={imgUrl(b.image_url)} alt={b.title || "Banner"} className="w-full h-full object-cover" draggable={false} />
      {b.title && <div className="keep-dark absolute inset-x-0 bottom-0 p-4 sm:p-6 bg-gradient-to-t from-black/85 to-transparent"><p className="font-display text-xl sm:text-3xl text-white">{b.title}</p></div>}
    </>
  );
  if (!b.link) return <div className="relative w-full h-full">{inner}</div>;
  return b.link.startsWith("/")
    ? <Link to={b.link} className="relative block w-full h-full" data-testid={`banner-link-${b.id}`}>{inner}</Link>
    : <a href={b.link} target="_blank" rel="noopener noreferrer" className="relative block w-full h-full" data-testid={`banner-link-${b.id}`}>{inner}</a>;
}

const noTrack = () => {};
const sendTrack = (id, ev) => axios.post(`${API}/banners/${id}/${ev}`).catch(() => {});

export default function BannerSlider({ state: st, city: ct, className = "", preview = false }) {
  const track = preview ? noTrack : sendTrack;
  const { location, setAskOpen } = useUserLocation();
  const state = st ?? location?.state;
  const city = ct ?? location?.city;
  const [banners, setBanners] = useState([]);
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const touch = useRef(null);

  useEffect(() => {
    setI(0);
    if (!state || !city) { setBanners([]); return; }
    let stale = false;
    axios.get(`${API}/banners`, { params: { state, city } }).then((r) => !stale && setBanners(r.data)).catch(() => !stale && setBanners([]));
    return () => { stale = true; };
  }, [state, city]);

  const n = banners.length;
  useEffect(() => {
    // Count one view per banner per browser session.
    const b = banners[i];
    if (!b || track === noTrack) return;
    const key = `csm_bv_${b.id}`;
    if (!sessionStorage.getItem(key)) { sessionStorage.setItem(key, "1"); track(b.id, "view"); }
  }, [i, banners, track]);
  const go = useCallback((d) => n && setI((x) => (x + d + n) % n), [n]);
  useEffect(() => {
    if (n < 2 || paused) return;
    const t = setInterval(() => go(1), 5000);
    return () => clearInterval(t);
  }, [n, paused, go]);

  const onTouchEnd = (e) => {
    if (touch.current == null) return;
    const dx = e.changedTouches[0].clientX - touch.current;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
    touch.current = null;
  };

  return (
    <div className={`relative overflow-hidden rounded-2xl border gold-line aspect-[16/7] sm:aspect-[16/5] bg-black group ${className}`}
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
      onTouchStart={(e) => { touch.current = e.touches[0].clientX; setPaused(true); }} onTouchEnd={(e) => { onTouchEnd(e); setPaused(false); }}
      data-testid="banner-slider" data-count={n}>
      {!n ? <DefaultBanner city={city} onPick={() => setAskOpen(true)} /> : (
        <div className="flex h-full transition-transform duration-700 ease-out" style={{ transform: `translateX(-${i * 100}%)` }}>
          {banners.map((b) => <div key={b.id} className="w-full h-full shrink-0" onClick={() => track(b.id, "click")} data-testid={`banner-slide-${b.id}`}><Slide b={b} /></div>)}
        </div>
      )}
      {n > 1 && <>
        <button onClick={() => go(-1)} className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 border border-white/10 hover:border-[#D4AF37] flex items-center justify-center opacity-80 sm:opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Previous banner" data-testid="banner-prev"><ChevronLeft className="w-4 h-4" /></button>
        <button onClick={() => go(1)} className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 border border-white/10 hover:border-[#D4AF37] flex items-center justify-center opacity-80 sm:opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Next banner" data-testid="banner-next"><ChevronRight className="w-4 h-4" /></button>
        <div className="absolute bottom-2.5 right-3 flex gap-1.5">
          {banners.map((b, k) => <button key={b.id} onClick={() => setI(k)} aria-label={`Banner ${k + 1}`} className={`h-1.5 rounded-full transition-[width,background-color] ${k === i ? "w-6 bg-[#D4AF37]" : "w-1.5 bg-white/40"}`} data-testid={`banner-dot-${k}`} />)}
        </div>
      </>}
    </div>
  );
}
